import { z } from 'zod';

import { apiSuccess, apiList } from '@services/apiSchemas';

import adminApiFetch from './apiService';

// ---- Schemas (mirror app/controllers/api/admin/v1/bundles_controller.rb) --

// `index` — for autocomplete dropdown. shape from L30-45 of the controller.
const BundleListItemSchema = z
  .object({
    id: z.string(),
    sku: z.string(),
    name: z.string(),
    bundle_products: z.array(
      z
        .object({
          product_id: z.string(),
          product_sku: z.string(),
          product_name: z.string(),
          quantity: z.number(),
          inventory_type_id: z.string().nullable(),
        })
        .passthrough(),
    ),
  })
  .passthrough();

// `get_records` — for the table view; includes aggregated availability info.
const BundleTableRowSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    sku: z.string(),
    barcode: z.string().nullable(),
    available_set_quantity: z.number(),
    bundle_items: z.array(
      z
        .object({
          product_name: z.string(),
          product_sku: z.string(),
          quantity: z.number(),
          available_quantity: z.number(),
        })
        .passthrough(),
    ),
    created_at: z.string(),
    activated: z.boolean(),
  })
  .passthrough();

// `show` — full record with relations.
const BundleDetailSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    sku: z.string(),
    barcode: z.string().nullable(),
    activated: z.boolean(),
    editable: z.boolean(),
    deletable: z.boolean(),
    bundle_products: z.array(
      z
        .object({
          id: z.string(),
          product_id: z.string(),
          quantity: z.number(),
          inventory_type_id: z.string().nullable(),
          product: z
            .object({
              id: z.string(),
              name: z.string(),
              sku: z.string(),
              barcode: z.string().nullable(),
            })
            .passthrough()
            .nullable()
            .optional(),
          inventory_type: z
            .object({ id: z.string(), name: z.string() })
            .passthrough()
            .nullable()
            .optional(),
        })
        .passthrough(),
    ),
  })
  .passthrough();

// `create` / `update` — the bare bundle record (no relations included on this branch)
const BundleSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    sku: z.string(),
    barcode: z.string().nullable(),
    activated: z.boolean(),
  })
  .passthrough();

// `get_ids` — array of UUIDs.
const IdsListSchema = z.array(z.string());

// `export` — async job kickoff.
const JobIdSchema = z.object({ job_id: z.string() }).passthrough();

// `import` — async job kickoff with the import record id.
const ImportJobSchema = z
  .object({ job_id: z.string(), import_id: z.string() })
  .passthrough();

// `delete` returns the standard envelope with no data of interest — keep loose.
const EmptyDataSchema = z.unknown();

// ---- Type exports ---------------------------------------------------------

export type BundleListItem = z.infer<typeof BundleListItemSchema>;
export type BundleTableRow = z.infer<typeof BundleTableRowSchema>;
export type BundleDetail = z.infer<typeof BundleDetailSchema>;
export type Bundle = z.infer<typeof BundleSchema>;

// ---- Input types ----------------------------------------------------------

interface FetchListParams {
  search?: string;
  limit?: number;
  merchant_id?: string;
}

// ---- Service --------------------------------------------------------------

const bundleService = {
  // 獲取組合包列表（用於下拉搜尋）
  fetchList: async (params: FetchListParams = {}) => {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.limit) queryParams.append('limit', String(params.limit));
    if (params.merchant_id) queryParams.append('merchant_id', params.merchant_id);

    const res = await adminApiFetch(`bundles?${queryParams.toString()}`, {
      method: 'GET',
    });
    return apiList(BundleListItemSchema).parse(await res.json());
  },

  // 獲取組合包列表（用於表格）
  getRecords: async (params: Record<string, unknown> = {}) => {
    const res = await adminApiFetch('bundles/get_records', {
      method: 'POST',
      body: params,
    });
    return apiList(BundleTableRowSchema).parse(await res.json());
  },

  // 獲取所有組合包 ID（用於篩選）
  getIds: async (params: Record<string, unknown> = {}) => {
    const res = await adminApiFetch('bundles/get_ids', {
      method: 'POST',
      body: params,
    });
    return apiSuccess(IdsListSchema).parse(await res.json());
  },

  // 獲取單個組合包
  get: async (id: string) => {
    const res = await adminApiFetch(`bundles/${id}`, {
      method: 'GET',
    });
    return apiSuccess(BundleDetailSchema).parse(await res.json());
  },

  // 創建組合包
  create: async (data: Record<string, unknown>) => {
    const res = await adminApiFetch('bundles', {
      method: 'POST',
      body: { bundle: data },
    });
    return apiSuccess(BundleSchema).parse(await res.json());
  },

  // 更新組合包
  update: async (id: string, data: Record<string, unknown>) => {
    const res = await adminApiFetch(`bundles/${id}`, {
      method: 'PUT',
      body: { bundle: data },
    });
    return apiSuccess(BundleSchema).parse(await res.json());
  },

  // 刪除組合包（軟刪除）
  delete: async (id: string) => {
    const res = await adminApiFetch(`bundles/${id}`, {
      method: 'DELETE',
    });
    return apiSuccess(EmptyDataSchema).parse(await res.json());
  },

  // 匯出組合包
  export: async (ids: string[]) => {
    const res = await adminApiFetch('bundles/export', {
      method: 'POST',
      body: { ids },
    });
    return apiSuccess(JobIdSchema).parse(await res.json());
  },

  // 匯入組合包
  import: async (formData: FormData) => {
    const res = await adminApiFetch('bundles/import', {
      method: 'POST',
      body: formData,
    });
    return apiSuccess(ImportJobSchema).parse(await res.json());
  },

  // 下載匯入模板
  downloadImportTemplate: async (locale: string) => {
    return adminApiFetch('import_templates/download', {
      method: 'POST',
      body: JSON.stringify({ type: 'bundle', locale }),
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
    });
  },
};

export { bundleService };
export default bundleService;
